import { useMutation } from '@apollo/client'
import { useCallback, useRef } from 'react'

import { useApolloClient } from '@apollo/client'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import {
    ProjectElementMetadataInput,
    UpdateElementInput,
} from '@deltek/specification-client/core/Models/projectElementInput'
import {
    UpdateProjectNuggetElementMutation,
    UpdateProjectProductFamilyElementMutation,
    UpdateProjectUniformatClassificationElementMutation,
} from 'api/graphQL/mutations/projectElement.mutations'
import { GetProjectProductFamilyViewQuery } from 'api/graphQL/queries/projectElements.queries'
import {
    UpdateProjectElementPropertiesResponse,
    UpdateProjectNuggetElementResponse,
    UpdateProjectProductFamilyElementResponse,
} from 'api/graphQL/types/projectElement.types'
import { useElementItemStore } from '../ElementItemStore'
import { useNotification } from '../services/notificationService'
import { getActiveTabLabel } from '../utils/workspaceEditorUtils'
import { useWorkspace2Store } from '../Workspace2Store'
import { useProductFetcher } from './useProductFetcher/useProductFetcher'

/**
 * Interface representing the properties required for Project Data API.
 */
export interface ProjectPropertiesApiProps {
    projectId: string
    contentTeamFromStudioPage: boolean
}

const getCurrentProjectState = () => {
    const {
        activeTab,
        isContentTeamFromStudioPage: isFromSMM,
        contentItemViewNodes,
    } = useWorkspace2Store.getState()
    const { elementItemValue } = useElementItemStore.getState()

    return {
        activeTab,
        isFromSMM,
        contentItemViewNodes,
        currentNodeId: elementItemValue.id,
        currentElementId: elementItemValue.elementId,
        currentPartType: getActiveTabLabel(activeTab),
    }
}

const getErrorMessage = (error: unknown, fallback: string): string => {
    if (error instanceof Error && error.message) return error.message
    if (typeof error === 'string' && error) return error
    return fallback
}

const getAllArticleIds = (
    contentItemViewNodes: any[] | undefined
): string[] => {
    const articleIds =
        contentItemViewNodes
            ?.filter((node) => node.textType === 'Article')
            .map((node) => node.id)
            .filter((id): id is string => {
                if (typeof id !== 'string') {
                    return false
                }
                return true
            }) || []
    return articleIds
}

/**
 * A custom hook that provides functionality to fetch and manage project data.
 *
 * @template T - The type of the props that extend `ProjectDataApiProps`.
 * @param {T} props - The properties required to initialize the hook.
 * @param {string} props.projectId - The ID of the project to fetch data for.
 * @returns {Object} An object containing the `fetchProjectData` function.
 * @returns {Function} returns.fetchProjectData - A function to fetch the project data.
 */
export const useProjectPropertiesMutation = <
    T extends ProjectPropertiesApiProps
>({
    projectId,
}: T) => {
    const apolloClient = useApolloClient()
    const { onContentViewFetchSuccess } = useProductFetcher({
        projectId,
        overrideContentItemNodes: false,
    })

    const notification = useNotification()

    // Track previous targetLOD
    const prevTargetLODRef = useRef<number | undefined>()

    const QUERY_DEPTH = 6

    const handleProductFamilyUpdateCompleted = useCallback(async () => {
        try {
            // Get fresh values from stores at mutation completion time
            const {
                currentNodeId,
                currentElementId,
                currentPartType,
                isFromSMM,
                contentItemViewNodes,
            } = getCurrentProjectState()

            // Get ALL article IDs from content tree
            const allArticleIds = getAllArticleIds(contentItemViewNodes)

            // Fetch updated data with ALL articles' children
            const response = await apolloClient.query({
                query: GetProjectProductFamilyViewQuery(QUERY_DEPTH),
                variables: {
                    elementId: currentElementId,
                    projectId,
                    partType: currentPartType,
                    rootArticleContentItemIds:
                        allArticleIds.length > 0 ? allArticleIds : null,
                    isTrackChangeToggleOn: false,
                    nodeId: currentNodeId,
                    isFromSMM,
                    features: [],
                },
                fetchPolicy: 'no-cache',
            })

            // Process the response and update the store (this will trigger tree rebuild)
            await onContentViewFetchSuccess(
                response.data,
                [],
                false,
                null,
                currentPartType
            )

            notification?.success?.({
                message: 'Changes Saved Successfully',
            })
        } catch (error) {
            // Handle errors during content refresh
            notification?.error?.({
                message: getErrorMessage(
                    error,
                    'Failed to refresh content after update'
                ),
            })
        }
    }, [apolloClient, projectId, onContentViewFetchSuccess, notification])

    const [updatePUCElementProperties] = useMutation<
        UpdateProjectElementPropertiesResponse,
        { input: UpdateElementInput }
    >(UpdateProjectUniformatClassificationElementMutation, {
        onError: (error) => {
            notification?.error?.({
                message: getErrorMessage(
                    error,
                    'Failed to update project properties'
                ),
            })
        },
    })

    const [updateProjectProductFamilyElement] = useMutation<
        UpdateProjectProductFamilyElementResponse,
        { input: UpdateElementInput }
    >(UpdateProjectProductFamilyElementMutation, {
        onCompleted: handleProductFamilyUpdateCompleted,
        onError: (error) => {
            notification?.error?.({
                message: getErrorMessage(
                    error,
                    'Failed to update project properties'
                ),
            })
        },
    })

    const [updateProjectNuggetElement] = useMutation<
        UpdateProjectNuggetElementResponse,
        { input: UpdateElementInput }
    >(UpdateProjectNuggetElementMutation, {
        onError: (error) => {
            notification?.error?.({
                message: getErrorMessage(
                    error,
                    'Failed to update project properties'
                ),
            })
        },
    })

    const updateProjectProperties = useCallback(
        async (
            finalSelectedValues: ProjectElementMetadataInput,
            projectElementNode: ProjectElementNode | null
        ) => {
            const inputValues = {
                variables: {
                    input: {
                        projectElementMetadataInput: finalSelectedValues,
                        elementId: projectElementNode?.elementId ?? '',
                        projectId: projectId ?? '',
                    },
                },
            }

            const isTargetLOSChange =
                finalSelectedValues.targetLOD !== undefined
            if (isTargetLOSChange) {
                notification?.loading?.({ message: 'Saving...' })
                prevTargetLODRef.current = finalSelectedValues.targetLOD
            }

            const isProductFamily =
                projectElementNode?.elementType === 'PRODUCTFAMILY'

            if (projectElementNode?.elementType === 'ASSEMBLY') {
                await updatePUCElementProperties(inputValues)
            } else if (isProductFamily) {
                await updateProjectProductFamilyElement(inputValues)
            } else {
                await updateProjectNuggetElement(inputValues)
            }

            if (isTargetLOSChange && !isProductFamily) {
                notification?.success?.({
                    message: 'Changes Saved Successfully',
                })
            }
        },
        [
            projectId,
            updateProjectNuggetElement,
            updateProjectProductFamilyElement,
            updatePUCElementProperties,
            notification,
        ]
    )
    return { updateProjectProperties }
}
